import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { findIndex } from 'lodash';

interface StreamMessage {
  _id: string;
  text: string;
  createdAt: string;
  [key: string]: any;
}

interface ConversationMap {
  [key: string]: {
    items: StreamMessage[];
    total: number;
    limit: number;
    loading: boolean;
  };
}

interface StreamChatState {
  activeConversation: any;
  sendMessage: {
    sending: boolean;
    success: boolean;
    error?: any;
  };
  receiveMessage: any;
  conversationMap: ConversationMap;
}

const initialState: StreamChatState = {
  activeConversation: {},
  sendMessage: {
    sending: false,
    success: false
  },
  receiveMessage: {},
  conversationMap: {}
};

const streamChatSlice = createSlice({
  name: 'streamChat',
  initialState,
  reducers: {
    resetAllStreamMessage(state, action?: PayloadAction<{ conversationId?: string }>) {
      if (action?.payload?.conversationId) {
        // Reset specific conversation
        if (state.conversationMap[action.payload.conversationId]) {
          delete state.conversationMap[action.payload.conversationId];
        }
      } else {
        // Reset all
        return initialState;
      }
    },
    resetStreamMessage(state, action: PayloadAction<string>) {
      const conversationId = action.payload;
      if (state.conversationMap[conversationId]) {
        state.conversationMap[conversationId] = {
          items: [],
          total: 0,
          limit: 0,
          loading: false
        };
      }
    },
    getStreamConversationSuccess(state, action: PayloadAction<any>) {
      state.activeConversation = action.payload;
    },
    loadStreamMessages(state, action: PayloadAction<{ conversationId: string; limit: number; offset?: number }>) {
      const { conversationId, limit } = action.payload;
      if (!state.conversationMap[conversationId]) {
        state.conversationMap[conversationId] = {
          items: [],
          total: 0,
          limit,
          loading: true
        };
      } else {
        state.conversationMap[conversationId].loading = true;
      }
    },
    loadStreamMessagesSuccess(state, action: PayloadAction<{ conversationId: string; data: StreamMessage[]; total: number }>) {
      const { conversationId, data, total } = action.payload;
      state.conversationMap[conversationId] = {
        items: data,
        total,
        limit: state.conversationMap[conversationId]?.limit || 20,
        loading: false
      };
    },
    loadMoreStreamMessages(state, action: PayloadAction<{ conversationId: string; limit: number; offset: number; type?: string }>) {
      const { conversationId } = action.payload;
      if (state.conversationMap[conversationId]) {
        state.conversationMap[conversationId].loading = true;
      }
    },
    loadMoreStreamMessagesSuccess(state, action: PayloadAction<{ conversationId: string; data: StreamMessage[]; total: number }>) {
      const { conversationId, data, total } = action.payload;
      if (state.conversationMap[conversationId]) {
        const existingItems = state.conversationMap[conversationId].items;
        state.conversationMap[conversationId] = {
          ...state.conversationMap[conversationId],
          items: [...existingItems, ...data],
          total,
          loading: false
        };
      }
    },
    sendStreamMessage(state, action: PayloadAction<any>) {
      state.sendMessage = {
        sending: true,
        success: false,
        error: undefined
      };
    },
    sendStreamMessageSuccess(state, action: PayloadAction<{ conversationId: string; message: StreamMessage }>) {
      const { conversationId, message } = action.payload;
      console.log('[sendStreamMessageSuccess] conversationId:', conversationId, 'message:', message);
      console.log('[sendStreamMessageSuccess] conversationMap before:', JSON.parse(JSON.stringify(state.conversationMap)));

      state.sendMessage = {
        sending: false,
        success: true
      };

      if (!state.conversationMap[conversationId]) {
        state.conversationMap[conversationId] = {
          items: [],
          total: 0,
          limit: 20,
          loading: false
        };
      }

      if (!state.conversationMap[conversationId].items) {
        state.conversationMap[conversationId].items = [];
      }

      const existingIndex = findIndex(state.conversationMap[conversationId].items, { _id: message._id });
      if (existingIndex > -1) {
        state.conversationMap[conversationId].items[existingIndex] = message;
      } else {
        state.conversationMap[conversationId].items.push(message);
      }

      console.log('[sendStreamMessageSuccess] conversationMap after:', JSON.parse(JSON.stringify(state.conversationMap)));
      console.log('[sendStreamMessageSuccess] activeConversation:', state.activeConversation);
    },
    sendStreamMessageFail(state, action: PayloadAction<any>) {
      state.sendMessage = {
        sending: false,
        success: false,
        error: action.payload
      };
    },
    receiveStreamMessageSuccess(state, action: PayloadAction<{ conversationId: string; message: StreamMessage }>) {
      const { conversationId, message } = action.payload;

      if (!conversationId || !message) {
        return;
      }

      if (!state.conversationMap[conversationId]) {
        state.conversationMap[conversationId] = {
          items: [],
          total: 0,
          limit: 20,
          loading: false
        };
      }

      // Ensure items array exists
      if (!state.conversationMap[conversationId].items) {
        state.conversationMap[conversationId].items = [];
      }

      const existingIndex = findIndex(state.conversationMap[conversationId].items, { _id: message._id });
      if (existingIndex === -1) {
        state.conversationMap[conversationId].items.push(message);
      }

      state.receiveMessage = message;
    },
    deleteMessage(state, action: PayloadAction<{ messageId: string }>) {
      // This action triggers the saga to delete a message
    },
    deleteMessageSuccess(state, action: PayloadAction<{ messageId: string }>) {
      const { messageId } = action.payload;

      // Remove from all conversation maps
      Object.keys(state.conversationMap).forEach((conversationId) => {
        state.conversationMap[conversationId].items =
          state.conversationMap[conversationId].items.filter(m => m._id !== messageId);
      });
    }
  }
});

export const {
  resetAllStreamMessage,
  resetStreamMessage,
  getStreamConversationSuccess,
  loadStreamMessages,
  loadStreamMessagesSuccess,
  loadMoreStreamMessages,
  loadMoreStreamMessagesSuccess,
  sendStreamMessage,
  sendStreamMessageSuccess,
  sendStreamMessageFail,
  receiveStreamMessageSuccess,
  deleteMessage,
  deleteMessageSuccess
} = streamChatSlice.actions;

export default streamChatSlice.reducer;